import React, { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

export type BadgeVariant = 'light' | 'solid';
export type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'gray'
  | 'purple';

interface BadgeProps {
  variant?: BadgeVariant;
  color?: BadgeColor;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

const colorClasses: Record<BadgeVariant, Record<BadgeColor, string>> = {
  light: {
    primary: 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-300',
    success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    error: 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-300',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  },
  solid: {
    primary: 'bg-brand-600 text-white',
    success: 'bg-green-600 text-white',
    error: 'bg-danger-600 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-600 text-white',
    gray: 'bg-gray-600 text-white',
    purple: 'bg-purple-600 text-white',
  },
};

export const Badge: React.FC<BadgeProps> = ({
  variant = 'light',
  color = 'primary',
  icon,
  children,
  className,
}) => {
  const classes = twMerge(
    clsx(
      'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
      colorClasses[variant][color],
      className
    )
  );

  return (
    <span className={classes}>
      {icon}
      {children}
    </span>
  );
};
